import { storageClient } from './client'
import type { Dataset, StorageDocument, StorageAuditLog, StorageApiKey, StorageApiKeyCreated, PdfTemplate, PdfTemplateConfig, PdfTemplateType, PdfJob } from '../types'

// ── Datasets ──────────────────────────────────────────────────────────────────
export const getDatasets = () =>
  storageClient.get<Dataset[]>('/datasets')

export const getDataset = (id: string) =>
  storageClient.get<Dataset>(`/datasets/${id}`)

export const createDataset = (data: { title: string; description?: string; archivePeriodDays: number; purgePeriodDays: number }) =>
  storageClient.post<Dataset>('/datasets', data)

export const updateDataset = (id: string, data: { title?: string; description?: string; archivePeriodDays?: number; purgePeriodDays?: number }) =>
  storageClient.put<Dataset>(`/datasets/${id}`, data)

export const deleteDataset = (id: string) =>
  storageClient.delete(`/datasets/${id}`)

export const getDatasetPermissions = (datasetId: string) =>
  storageClient.get<import('../types').DatasetPermission[]>(`/datasets/${datasetId}/permissions`)

export const addDatasetPermission = (datasetId: string, data: { serviceId: string; permissionType: string }) =>
  storageClient.post('/datasets/permissions', { datasetId, ...data })

export const removeDatasetPermission = (datasetId: string, serviceId: string) =>
  storageClient.delete('/datasets/permissions', { data: { datasetId, serviceId } })

// ── Documents ─────────────────────────────────────────────────────────────────
export const getAllDocuments = () =>
  storageClient.get<StorageDocument[]>('/documents')

export const getDocument = (id: string) =>
  storageClient.get<StorageDocument>(`/documents/${id}`)

// The tab is opened synchronously, inside the click, and only pointed at the PDF once the
// authenticated fetch resolves — opening it after the await instead lets popup blockers silently
// drop it whenever the fetch is slow (e.g. it first had to refresh an expired token). Must be
// called directly from a click handler. Throws (and closes the placeholder tab) on failure.
export const viewDocumentInTab = async (id: string) => {
  const win = window.open('', '_blank')
  try {
    const res = await storageClient.get(`/documents/${id}/download`, { responseType: 'blob' })
    const url = URL.createObjectURL(res.data as Blob)
    if (win) win.location.href = url
    else window.location.assign(url)
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (err) {
    win?.close()
    throw err
  }
}

// Same authenticated blob fetch as viewDocumentInTab, but hands back the object URL instead of
// opening it — for a Plate's inline "_document" column, which embeds the URL directly (e.g. in an
// <iframe>) rather than launching a new tab. Caller owns the URL's lifetime and must revoke it.
export const getDocumentBlobUrl = async (id: string): Promise<string> => {
  const res = await storageClient.get(`/documents/${id}/download`, { responseType: 'blob' })
  return URL.createObjectURL(res.data as Blob)
}

export const getDocumentsByDataset = (datasetId: string) =>
  storageClient.get<StorageDocument[]>(`/datasets/${datasetId}/documents`)

export const uploadDocument = (data: { datasetId: string; title: string; description?: string; file: File; metadata?: string }) => {
  const form = new FormData()
  form.append('file', data.file)
  form.append('title', data.title)
  form.append('datasetId', data.datasetId)
  if (data.description) form.append('description', data.description)
  if (data.metadata) form.append('metadata', data.metadata)
  return storageClient.post<StorageDocument>('/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } })
}

export const updateDocument = (id: string, data: { title?: string; description?: string }) =>
  storageClient.put<StorageDocument>(`/documents/${id}`, data)

export const deleteDocument = (id: string) =>
  storageClient.delete(`/documents/${id}`)

const EXTENSION_BY_MIME: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'text/csv': '.csv',
  'image/png': '.png',
  'image/jpeg': '.jpg',
}

export const downloadDocument = async (id: string, filename: string) => {
  const res = await storageClient.get(`/documents/${id}/download`, { responseType: 'blob' })
  const blob = res.data as Blob
  const url = URL.createObjectURL(blob)
  // Document titles (e.g. "Purchase Order 2026-000001") usually carry no extension, which made the
  // saved file unopenable by double-click — append one from the MIME type when it's missing.
  const ext = EXTENSION_BY_MIME[blob.type]
  const a = document.createElement('a')
  a.href = url
  a.download = ext && !filename.toLowerCase().endsWith(ext) ? filename + ext : filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// ── Audit Logs ────────────────────────────────────────────────────────────────
export const getStorageAuditLogs = (params?: { businessId?: string; serviceId?: string; action?: string; limit?: number }) =>
  storageClient.get<StorageAuditLog[]>('/audit-logs', { params })

// ── Config ────────────────────────────────────────────────────────────────────
export const getStorageConfig = () =>
  storageClient.get<import('../types').ServiceConfigParam[]>('/config')

export const setStorageConfig = (key: string, value: string) =>
  storageClient.post('/config', { [key]: value })

// ── API Keys ──────────────────────────────────────────────────────────────────
export const getStorageApiKeys = () =>
  storageClient.get<StorageApiKey[]>('/keys')

export const createStorageApiKey = (data: { name: string; serviceId: string; lifetimeYears: 1 | 2 | 3 }) =>
  storageClient.post<StorageApiKeyCreated>('/keys', data)

export const revokeStorageApiKey = (id: string) =>
  storageClient.delete(`/keys/${id}`)

// ── PDF Composition ───────────────────────────────────────────────────────────

export const getPdfTemplates = () =>
  storageClient.get<PdfTemplate[]>('/pdf/templates')

export const getPdfTemplate = (id: string) =>
  storageClient.get<PdfTemplate>(`/pdf/templates/${id}`)

export const createPdfTemplate = (data: { name: string; description?: string; templateType: PdfTemplateType; config: PdfTemplateConfig; htmlOverride?: string }) =>
  storageClient.post<PdfTemplate>('/pdf/templates', data)

export const clonePdfTemplate = (id: string, name?: string) =>
  storageClient.post<PdfTemplate>(`/pdf/templates/${id}/clone`, { name })

export const updatePdfTemplate = (id: string, data: { name?: string; description?: string; config?: PdfTemplateConfig; htmlOverride?: string | null }) =>
  storageClient.put<PdfTemplate>(`/pdf/templates/${id}`, data)

export const deletePdfTemplate = (id: string) =>
  storageClient.delete(`/pdf/templates/${id}`)

export const previewPdfTemplate = (id: string, payload?: Record<string, unknown>) =>
  storageClient.post<string>(`/pdf/templates/${id}/preview`, { payload: payload ?? {} }, { responseType: 'text' })

export const generatePdf = (data: { templateId: string; payload?: Record<string, unknown>; mode?: 'sync' | 'async'; datasetId?: string }) =>
  storageClient.post<{ jobId: string; documentId?: string; documentUrl?: string; status: string }>('/pdf/generate', data)

export const getPdfJob = (id: string) =>
  storageClient.get<PdfJob>(`/pdf/jobs/${id}`)

export const getPdfJobs = () =>
  storageClient.get<PdfJob[]>('/pdf/jobs')
